import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Header from '../components/Header';

import Footer from '../components/Footer';
import SEO from '../components/SEO';

const Highlight = ({ children }) => (
  <span className="text-blue-600 font-bold">{children}</span>
);

const Quote = ({ text, attribution }) => (
  <blockquote className="my-10 pl-6 border-l-4 border-blue-600 bg-gray-50 rounded-r-lg py-6 pr-6" data-testid="origin-quote">
    <p className="text-lg italic text-gray-700 leading-relaxed mb-3">{text}</p>
    <cite className="text-sm font-bold text-gray-900 not-italic">{attribution}</cite>
  </blockquote>
);

export default function OriginStory() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO
        title="Origin Story - Why We Started Building Forsyt"
        description="86% of GTM leaders we spoke to said they'd pay for a tool that tells reps what to do next. This is the story of how Forsyt was born from a real market signal."
        path="/origin-story"
      />

      {/* Hero Section */}
      <section className="bg-white pt-44 pb-16 border-b border-gray-100" data-testid="origin-hero">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-blue-600 mb-6">Origin Story</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight mb-6">
            Bridging the Gap Between Lived Experience and an{' '}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">86% MVP Demand</span>
          </h1>
          <p className="text-lg text-gray-500 leading-relaxed max-w-2xl mx-auto">
            The transparent, data-driven story of how a hypothesis born from frustration evolved into a platform that CROs actively demanded to test.
          </p>
        </div>
      </section>

      {/* Article Body */}
      <article className="py-20" data-testid="origin-body">
        <div className="max-w-[740px] mx-auto px-6">

          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            Every transformative product begins with a fracture, a breaking point where the accepted way of doing things is no longer tolerable. For us at Forsyt, we didn't have to look hard to find that fracture. We didn't manufacture a problem in a boardroom just to sell a solution. We lived the problem.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-12">
            This is the transparent, data-driven story of how a hypothesis born from frustration evolved into a platform that Chief Revenue Officers (CROs) actively demanded to test.
          </p>

          {/* Section: The Catalyst */}
          <h3 className="text-2xl font-bold text-gray-900 mt-16 mb-6" data-testid="section-catalyst">
            The Catalyst: Udit and the 100-Persona Deep Dive
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            It started with our CEO, Udit. Having spent years navigating the complex, often chaotic realities of scaling businesses and driving growth, he recognized a glaring disconnect in the revenue technology space. Tools were being built for management oversight, not for the people actually driving the revenue.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            But gut feeling isn't a business strategy. To build something genuinely disruptive, we needed undeniable validation. We threw out our internal assumptions and committed to intense, ground-level primary research.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Over the course of several months, we sat down with <Highlight>100 distinct revenue personas</Highlight>, from frontline Account Executives and SDRs to RevOps leaders and VPs of Sales. We didn't pitch them anything. We just listened. We mapped out their daily workflows, their operational bottlenecks, and the friction points that drained their time and morale.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            The feedback was raw, consistent, and illuminating:
          </p>

          <Quote
            text="We have a tech stack that costs us six figures a year, yet my reps are still downloading CSVs to figure out who they need to call today. The tools are silos, not solutions."
            attribution="- VP of Revenue Operations, Mid-Market SaaS"
          />

          <Quote
            text="I don't need another dashboard telling me what happened last month. I need something that tells my team what to do right now, in this exact hour, to save a deal."
            attribution="- Head of Sales, Enterprise Tech"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-12">
            Once that phase was complete, the noise cleared. The core issue staring back at us was undeniable: revenue teams didn't need more data; they needed that data translated into immediate, actionable intelligence. We knew exactly what had to be built.
          </p>

          {/* Section: The Validation */}
          <h3 className="text-2xl font-bold text-gray-900 mt-16 mb-6" data-testid="section-validation">
            The Validation: Two Screens and a Bold Hypothesis
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            In the startup world, the biggest trap is spending months building a bloated product in the dark, hoping the market will catch up. We refused to do that.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Instead of writing thousands of lines of code for features no one had asked for, we designed just <Highlight>two screens</Highlight>. It was a small, highly focused sandbox environment that demonstrated exactly how we intended to solve the core issue. With those two screens in hand, we started booking calls with Chief Revenue Officers.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            We set a benchmark for ourselves.
          </p>

          <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 my-8">
            <p className="text-lg text-gray-800 leading-relaxed">
              <span className="font-bold">Our Hypothesis:</span>{' '}
              <em>If we can show these CROs that we truly understand and can solve this core workflow issue, we should be able to hit a 60% acceptance rate for our MVP testing phase.</em>
            </p>
          </div>

          {/* Section: The Reality */}
          <h3 className="text-2xl font-bold text-gray-900 mt-16 mb-6" data-testid="section-reality">
            The Reality: Shattering Expectations
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The response to those two screens completely derailed our expectations, in the best way possible. Out of the 100 interviews we conducted with revenue leaders, we didn't just scrape by our 60% target.
          </p>

          <div className="my-10 text-center" data-testid="metric-highlight">
            <p className="text-5xl sm:text-6xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-3">86%</p>
            <p className="text-lg font-medium text-gray-600">acceptance rate</p>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The resonance was immediate and visceral. Because the product was built on actual, lived experiences and validated by 100 of their peers, the CROs recognized their own struggles instantly.
          </p>

          <Quote
            text="I've taken dozens of vendor calls this quarter, and this is the first time someone has actually shown me a workflow that mimics how my brain works when I'm trying to forecast. How soon can we get our hands on this?"
            attribution="- Chief Revenue Officer, Global Fintech Provider"
          />

          <Quote
            text="You aren't just giving me another CRM overlay. You're giving me an operational brain for my sales floor. Put us on the waitlist."
            attribution="- SVP of Sales, E-commerce Logistics"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-12">
            They didn't just understand the vision; they explicitly wanted the MVP right then and there.
          </p>

          {/* Section: What's Next */}
          <h3 className="text-2xl font-bold text-gray-900 mt-16 mb-6" data-testid="section-next">
            What's Next: The Rollout and the Journey Ahead
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Today, that vision is a reality. We have officially rolled out our first batch, getting the Forsyt MVP into the hands of those early believers who saw the potential in those two simple screens.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            But this is just the beginning. Right now, we are managing an active pipeline of <Highlight>80+ waitlisted teams</Highlight> who are ready to come on board and transform their revenue operations.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            We promised ourselves from day one that we wouldn't build Forsyt in a silo. We are going to keep sharing our journey, our pivots, our challenges, and our wins with you. This isn't just a product; it's a movement toward a smarter, more intuitive way to work.
          </p>

          <p className="text-xl font-semibold text-gray-900 mb-12">
            Welcome to Forsyt.
          </p>

        </div>
      </article>

      {/* Bottom CTA */}
      <section className="bg-gray-50 border-t border-gray-100 py-20" data-testid="origin-cta">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Ready to see what the <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">86%</span> saw?
          </h2>
          <p className="text-lg text-gray-500 mb-8">
            Join the 80+ teams in our pipeline waiting to transform their revenue operations.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://calendar.app.google/nswB9pt6ALLZ8xoy7"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-base font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
              data-testid="origin-cta-button"
            >
              Join the MVP Waitlist
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold rounded-lg border border-gray-300 text-gray-700 hover:text-gray-900 hover:border-gray-400 transition-all duration-200"
              data-testid="origin-demo-button"
            >
              Try the Live Demo
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
